import { buildFetchConfig } from '@/utils/fetchUtils';
import { HttpError } from './skillsApi';
import type {
  FileUploadState,
  AttachmentPresignRequest,
  AttachmentPresignResponse,
  AttachmentCompleteMultipartRequest,
  AttachmentUploadResult,
} from '@/types/chat';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB chunks for multipart
const LARGE_FILE_THRESHOLD = 20 * 1024 * 1024; // 20MB - files larger than this use presigned URL

/**
 * Determine the file_type label from parsed preview metadata
 */
const resolveFileType = (
  headers: string[] | undefined,
  geojsonPreview:
    | {
        propertyNames: string[];
        featureCount: number;
        geometryType: string | null;
      }
    | undefined,
  shapefileInfo:
    | { isShapefile: boolean; shpFileName: string | null }
    | undefined,
): string | undefined => {
  if (headers) return 'csv';
  if (geojsonPreview) return 'geojson';
  if (shapefileInfo?.isShapefile) return 'shapefile';
  return undefined;
};

/**
 * Generate a unique filename to avoid S3 collisions
 * Format: originalName_timestamp_randomId.extension
 * Example: data_1704825600000_a1b2c3.csv
 */
const generateUniqueFilename = (originalFilename: string): string => {
  const lastDotIndex = originalFilename.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;

  const name = hasExtension
    ? originalFilename.substring(0, lastDotIndex)
    : originalFilename;
  const extension = hasExtension
    ? originalFilename.substring(lastDotIndex)
    : '';

  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 8);

  return `${name}_${timestamp}_${randomId}${extension}`;
};

/**
 * Check if a file requires the asset import feature flag (.geojson or shapefile .zip)
 */
export const isImportFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return name.endsWith('.geojson') || name.endsWith('.shp');
};

/**
 * Check if a file is a video file
 */
export const isVideoFile = (file: File): boolean => {
  const videoExtensions = [
    '.mp4',
    '.mov',
    '.avi',
    '.webm',
    '.mkv',
    '.flv',
    '.wmv',
  ];
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return videoExtensions.includes(ext) || file.type.startsWith('video/');
};

/**
 * Check if a file should use presigned URL upload
 * - Video files always use presigned URL
 * - Large files (> 20MB) use presigned URL
 */
const shouldUsePresignedUrl = (file: File): boolean => {
  return isVideoFile(file) || file.size > LARGE_FILE_THRESHOLD;
};

/**
 * Get presigned URL(s) for file upload
 */
export const getPresignedUrl = async (
  request: AttachmentPresignRequest,
  signal?: AbortSignal,
): Promise<AttachmentPresignResponse> => {
  const { baseURL, headers } = buildFetchConfig();

  const response = await fetch(`${baseURL}/attachments/presign`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new HttpError(
      response.status,
      errorText,
      `Failed to get presigned URL: ${response.status} - ${errorText}`,
    );
  }

  return response.json();
};

/**
 * Upload file directly (small files)
 */
export const uploadFileDirect = async (
  file: File,
  uniqueFilename: string,
  signal?: AbortSignal,
): Promise<AttachmentUploadResult> => {
  const { baseURL, headers } = buildFetchConfig();
  const formData = new FormData();

  // Create a new File with the unique filename
  const renamedFile = new File([file], uniqueFilename, { type: file.type });
  formData.append('files', renamedFile);

  // Remove Content-Type header to let browser set multipart boundary
  const headersWithoutContentType = Object.fromEntries(
    Object.entries(headers).filter(([key]) => key !== 'Content-Type'),
  );

  const response = await fetch(`${baseURL}/attachments/upload`, {
    method: 'POST',
    headers: headersWithoutContentType,
    body: formData,
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new HttpError(
      response.status,
      errorText,
      `Failed to upload file: ${response.status} - ${errorText}`,
    );
  }

  const results = await response.json();
  return results[0]; // Return first result since we upload one file
};

/**
 * Shared XHR plumbing for presigned PUT uploads: wires up progress, load,
 * error, and abort handling, plus AbortSignal cancellation. Callers extract
 * whatever they need from the XHR in `onLoad` (which may throw to reject).
 */
const xhrRequest = <T>(
  url: string,
  body: Blob,
  options: {
    contentType: string;
    signal?: AbortSignal;
    onProgress?: (progress: number) => void;
    onLoad: (xhr: XMLHttpRequest) => T;
    networkErrorMessage: string;
    abortMessage: string;
  },
): Promise<T> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable && options.onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        options.onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      try {
        resolve(options.onLoad(xhr));
      } catch (err) {
        reject(err);
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error(options.networkErrorMessage));
    });

    xhr.addEventListener('abort', () => {
      reject(new DOMException(options.abortMessage, 'AbortError'));
    });

    if (options.signal) {
      options.signal.addEventListener('abort', () => {
        xhr.abort();
      });
    }

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', options.contentType);
    xhr.send(body);
  });
};

/**
 * Upload a single file to presigned URL with progress tracking
 */
const uploadToPresignedUrl = async (
  file: File,
  presignedUrl: string,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void,
): Promise<void> =>
  xhrRequest<void>(presignedUrl, file, {
    contentType: file.type || 'application/octet-stream',
    signal,
    onProgress,
    networkErrorMessage: 'Upload failed due to network error',
    abortMessage: 'Upload aborted',
    onLoad: xhr => {
      if (xhr.status < 200 || xhr.status >= 300) {
        throw new Error(`Upload failed with status: ${xhr.status}`);
      }
    },
  });

/**
 * Upload a chunk and return ETag
 */
const uploadChunk = async (
  chunk: Blob,
  presignedUrl: string,
  contentType: string,
  signal?: AbortSignal,
): Promise<string> =>
  xhrRequest<string>(presignedUrl, chunk, {
    contentType,
    signal,
    networkErrorMessage: 'Chunk upload failed due to network error',
    abortMessage: 'Chunk upload aborted',
    onLoad: xhr => {
      if (xhr.status < 200 || xhr.status >= 300) {
        throw new Error(`Chunk upload failed with status: ${xhr.status}`);
      }
      const etag = xhr.getResponseHeader('ETag');
      if (!etag) {
        throw new Error('No ETag in response');
      }
      return etag.replace(/"/g, '');
    },
  });

/**
 * Complete multipart upload
 */
export const completeMultipartUpload = async (
  request: AttachmentCompleteMultipartRequest,
  signal?: AbortSignal,
): Promise<void> => {
  const { baseURL, headers } = buildFetchConfig();

  const response = await fetch(`${baseURL}/attachments/complete-multipart`, {
    method: 'POST',
    headers,
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new HttpError(
      response.status,
      errorText,
      `Failed to complete multipart upload: ${response.status} - ${errorText}`,
    );
  }
};

/**
 * Parse GeoJSON preview from file (for .geojson and .json files only)
 */
const parseGeoJSONPreview = async (
  file: File,
): Promise<
  | {
      propertyNames: string[];
      featureCount: number;
      geometryType: string | null;
    }
  | undefined
> => {
  const name = file.name.toLowerCase();
  if (!name.endsWith('.geojson') && !name.endsWith('.json')) return undefined;

  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        // Attempt to parse the (possibly truncated) text as JSON. If the
        // truncation broke the JSON structure we fall back to undefined and
        // let the server-side parse_import_file handle the full file.
        let obj: unknown;
        try {
          obj = JSON.parse(text);
        } catch {
          // Truncated JSON — metadata extraction not possible from preview;
          // server-side parse_import_file will handle the full file.
          resolve(undefined);
          return;
        }
        if (
          typeof obj !== 'object' ||
          obj === null ||
          (obj as Record<string, unknown>).type !== 'FeatureCollection' ||
          !Array.isArray((obj as Record<string, unknown>).features)
        ) {
          resolve(undefined);
          return;
        }
        const features = (obj as Record<string, unknown>).features as Array<
          Record<string, unknown>
        >;
        const props =
          features.length > 0
            ? Object.keys(
                (features[0].properties as Record<string, unknown>) || {},
              )
            : [];
        const geomType =
          features.length > 0
            ? (
                features[0].geometry as
                  | Record<string, unknown>
                  | null
                  | undefined
              )?.type
            : null;
        resolve({
          propertyNames: props,
          featureCount: features.length,
          geometryType: (geomType as string) ?? null,
        });
      } catch (outerErr) {
        console.warn(
          `parseGeoJSONPreview: property extraction failed for ${file.name}`,
          outerErr,
        );
        resolve(undefined);
      }
    };
    reader.onerror = () => {
      console.warn(
        `parseGeoJSONPreview: FileReader error reading ${file.name}`,
        reader.error,
      );
      resolve(undefined);
    };
    // Read only the first 64KB — enough for metadata from most GeoJSON files.
    // If the truncated slice is not valid JSON, we resolve undefined and let
    // the server-side parse_import_file handle the complete file.
    reader.readAsText(file.slice(0, 65536));
  });
};

/**
 * Detect if a zip file is likely a Shapefile.
 *
 * This is a filename-only heuristic: every `.zip` is treated as a
 * candidate Shapefile and flagged for server-side validation. The
 * server's `parse_import_file` tool opens the archive and rejects
 * zips that don't contain the required `.shp`/`.dbf` members.
 */
const detectShapefile = (
  file: File,
): { isShapefile: boolean; shpFileName: string | null } | undefined => {
  if (!file.name.toLowerCase().endsWith('.zip')) return undefined;
  return {
    isShapefile: true,
    shpFileName: file.name.replace(/\.zip$/i, '.shp'),
  };
};

/**
 * Parse CSV headers from file (for CSV files only)
 */
const parseCSVHeaders = async (file: File): Promise<string[] | undefined> => {
  if (!file.name.toLowerCase().endsWith('.csv')) {
    return undefined;
  }

  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const firstLine = text.split('\n')[0];
        const headers = firstLine
          .split(',')
          .map(h => h.trim().replace(/^["']|["']$/g, ''));
        resolve(headers);
      } catch {
        resolve(undefined);
      }
    };
    reader.onerror = () => resolve(undefined);
    // Read only first 4KB to get headers
    reader.readAsText(file.slice(0, 4096));
  });
};

/**
 * Main upload function that handles different upload strategies:
 * - Small non-video files (< 20MB): Direct upload via /attachments/upload
 * - Video files or large files (>= 20MB): Presigned URL upload (single or multipart)
 */
export const uploadAttachment = async (
  file: File,
  onProgress?: (state: Partial<FileUploadState>) => void,
  signal?: AbortSignal,
): Promise<AttachmentUploadResult> => {
  const contentType = file.type;
  const originalFilename = file.name;
  const uniqueFilename = generateUniqueFilename(originalFilename);

  // Notify: initializing
  onProgress?.({
    status: 'uploading',
    progress: 0,
  });

  // For small non-video files, use direct upload
  if (!shouldUsePresignedUrl(file)) {
    onProgress?.({
      status: 'uploading',
      progress: 10,
    });

    const result = await uploadFileDirect(file, uniqueFilename, signal);

    // Parse CSV headers if applicable
    const headers = await parseCSVHeaders(file);
    const geojsonPreview = await parseGeoJSONPreview(file);
    const shapefileInfo = detectShapefile(file);

    // Determine file_type
    const file_type = resolveFileType(headers, geojsonPreview, shapefileInfo);

    onProgress?.({
      status: 'completed',
      progress: 100,
    });

    return {
      ...result,
      filename: originalFilename, // Keep original filename for display
      headers,
      geojson_properties: geojsonPreview?.propertyNames,
      feature_count: geojsonPreview?.featureCount,
      geometry_type: geojsonPreview?.geometryType ?? undefined,
      file_type,
    };
  }

  // For video files or large files, use presigned URL upload
  const presignResponse = await getPresignedUrl(
    {
      filename: uniqueFilename, // Use unique filename for S3
      file_size: file.size,
      content_type: contentType,
    },
    signal,
  );

  const s3Key = presignResponse.s3_key || '';

  // Check if multipart upload
  if (
    presignResponse.upload_type === 'multipart' &&
    presignResponse.presigned_urls
  ) {
    // Multipart upload for large files
    const { presigned_urls, upload_id, chunk_size } = presignResponse;

    if (!upload_id || !presigned_urls.length) {
      throw new Error('Missing multipart upload configuration');
    }

    const actualChunkSize = chunk_size || CHUNK_SIZE;
    const totalParts = presigned_urls.length;
    const parts: Array<{ part_number: number; etag: string }> = [];

    // Upload each chunk
    for (let i = 0; i < totalParts; i++) {
      if (signal?.aborted) {
        throw new DOMException('Upload aborted', 'AbortError');
      }

      const partInfo = presigned_urls[i];
      const start = i * actualChunkSize;
      const end = Math.min(start + actualChunkSize, file.size);
      const chunk = file.slice(start, end);

      const etag = await uploadChunk(
        chunk,
        partInfo.presigned_url,
        contentType,
        signal,
      );

      parts.push({
        part_number: partInfo.part_number,
        etag,
      });

      // Reserve 10% for completion
      const uploadProgress = Math.round(((i + 1) / totalParts) * 90);
      onProgress?.({
        status: 'uploading',
        progress: uploadProgress,
      });
    }

    // Complete multipart upload
    onProgress?.({
      status: 'uploading',
      progress: 95,
    });

    await completeMultipartUpload(
      {
        upload_id,
        s3_key: s3Key,
        parts,
      },
      signal,
    );
  } else if (presignResponse.presigned_url) {
    // Single presigned URL upload
    await uploadToPresignedUrl(
      file,
      presignResponse.presigned_url,
      signal,
      progress => {
        onProgress?.({
          status: 'uploading',
          progress,
        });
      },
    );
  } else {
    throw new Error('No presigned URL returned for video upload');
  }

  // Detect file type and extract preview metadata.
  // Works for large files too — preview readers only read the first
  // few KB, and Shapefile detection is filename-only.
  const headers = await parseCSVHeaders(file);
  const geojsonPreview = await parseGeoJSONPreview(file);
  const shapefileInfo = detectShapefile(file);

  const file_type = resolveFileType(headers, geojsonPreview, shapefileInfo);

  // Done
  onProgress?.({
    status: 'completed',
    progress: 100,
  });

  return {
    filename: originalFilename, // Keep original filename for display
    s3_key: s3Key,
    s3_url: presignResponse.s3_url || '',
    size: file.size,
    content_type: contentType,
    headers,
    geojson_properties: geojsonPreview?.propertyNames,
    feature_count: geojsonPreview?.featureCount,
    geometry_type: geojsonPreview?.geometryType ?? undefined,
    file_type,
  };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Get file icon based on file extension
 */
export const getFileIcon = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const iconMap: Record<string, string> = {
    csv: '📊',
    xlsx: '📊',
    xls: '📊',
    pdf: '📄',
    doc: '📝',
    docx: '📝',
    txt: '📝',
    mp4: '🎬',
    mov: '🎬',
    avi: '🎬',
    webm: '🎬',
    jpg: '🖼️',
    jpeg: '🖼️',
    png: '🖼️',
    gif: '🖼️',
    zip: '📦',
    rar: '📦',
    json: '📋',
    xml: '📋',
  };
  return iconMap[ext] || '📎';
};
